import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';

const UsersIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const SortIcon = ({ direction }) => (
  <svg className={`w-4 h-4 inline-block ml-1 ${direction ? 'opacity-100' : 'opacity-30'}`}
       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    {direction === 'asc' ? (
      <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z" />
    ) : (
      <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z" />
    )}
  </svg>
);

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export default function UsersStatistics({ fetchWithAuth }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState(null);
  const [freshSince, setFreshSince] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'total_photos', direction: 'desc' });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/statistics/users`);
        if (response.error) throw new Error(response.error);
        const data = await response.json();
        setUsers(data.data);
        setFreshSince(data.fresh_since);
      } catch (error) {
        console.error('Error fetching users statistics:', error);
      }
    };

    fetchUsers();
  }, [fetchWithAuth]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedUsers = users ? [...users].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (sortConfig.key === 'username') {
      // Tri alphabétique pour username
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      // Tri numérique pour toutes les autres colonnes
      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    }
  }) : [];

  const TableHeader = ({ label, sortKey }) => (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center">
        {label}
        <SortIcon direction={sortConfig.key === sortKey ? sortConfig.direction : null} />
      </div>
    </th>
  );

  const columns = [
    { label: t("User"), key: "username" },
    { label: t("Duplicate photos"), key: "total_duplicates" },
    { label: t("Distinct projects"), key: "distinct_projects" },
    { label: t("Deleted photos"), key: "deleted_photos" },
    { label: t("Total photos"), key: "total_photos" },
    { label: t("Days of activity"), key: "days_between_oldest_newest" },
    { label: t("Days since last photo"), key: "days_since_last_photo" }
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold">{t('User Statistics')}</h2>
        </div>
        {freshSince && (
          <span className="text-sm text-gray-500">
            {t('Last updated')}: {new Date(freshSince).toLocaleString()}
          </span>
        )}
      </div>

      {!users ? (
        <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-center text-gray-600 dark:text-gray-400 max-w-lg">
              {t('User statistics are resource-intensive and may take up to 20 seconds to load!')}
              <br />
              {t('Please be patient while they are being displayed.')}
              <br />
              {t('Signed: the database')}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="overflow-x-auto max-h-[calc(100vh-12rem)] rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  {columns.map((column) => (
                    <TableHeader key={column.key} label={column.label} sortKey={column.key} />
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {sortedUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.total_duplicates)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.distinct_projects)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.deleted_photos)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.total_photos)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.days_between_oldest_newest)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(user.days_since_last_photo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
